import React, { useState } from "react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarIcon, Clock } from "lucide-react";

const TIME_SLOTS = (() => {
  const out = [];
  for (let h = 5; h <= 23; h++) {
    for (const m of [0, 15, 30, 45]) {
      out.push(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
    }
  }
  return out;
})();

function fmtLong(d) {
  if (!d) return "";
  return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

/**
 * value: ISO string yyyy-mm-ddThh:mm
 * onChange: (iso)
 * min, max: ISO bounds for the calendar.
 */
export default function DateTimePicker({ value, onChange, min, max, testId = "dt" }) {
  const [open, setOpen] = useState(false);
  const [openTime, setOpenTime] = useState(false);

  const parsed = value ? new Date(value) : null;
  const date = parsed && !isNaN(parsed) ? parsed : null;
  const time = date
    ? `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`
    : "09:00";

  const setDate = (d) => {
    if (!d) return;
    const out = new Date(d);
    const [h, mm] = time.split(":");
    out.setHours(parseInt(h, 10), parseInt(mm, 10), 0, 0);
    const iso = isoLocal(out);
    onChange(iso);
    setOpen(false);
  };

  const setTime = (t) => {
    const base = date || new Date();
    const [h, mm] = t.split(":");
    base.setHours(parseInt(h, 10), parseInt(mm, 10), 0, 0);
    onChange(isoLocal(base));
    setOpenTime(false);
  };

  const isoLocal = (d) => {
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const minDate = min ? new Date(min) : undefined;
  const maxDate = max ? new Date(max) : undefined;

  return (
    <div className="grid grid-cols-[1fr_120px] gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-date-btn`}
            className="iq-input text-left flex items-center justify-between gap-2"
          >
            <span className={date ? "text-cream" : "text-cream-dim/50"}>{date ? fmtLong(date) : "Pick a date"}</span>
            <CalendarIcon className="h-4 w-4 text-gold shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-panel border-panel-border" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={(d) => (minDate && d < minDate) || (maxDate && d > maxDate)}
            initialFocus
            className="text-cream"
            classNames={{
              caption_label: "font-display text-lg tracking-wider text-cream",
              head_cell: "text-cream-dim text-[10px] uppercase tracking-widest font-display w-9",
              nav_button: "h-7 w-7 bg-ink border border-panel-border text-gold rounded-sm hover:bg-panel-border",
              day: "h-9 w-9 p-0 font-body text-cream hover:bg-panel-border rounded-sm",
              day_selected: "bg-gold text-ink font-display tracking-wider hover:bg-gold hover:text-ink",
              day_today: "border border-gold/50",
              day_outside: "text-cream-dim/30",
              day_disabled: "text-cream-dim/20 cursor-not-allowed",
            }}
          />
        </PopoverContent>
      </Popover>

      <Popover open={openTime} onOpenChange={setOpenTime}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-time-btn`}
            className="iq-input text-left flex items-center justify-between gap-2"
          >
            <span className="text-cream font-display tracking-widest">{time}</span>
            <Clock className="h-4 w-4 text-gold shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-40 p-0 bg-panel border-panel-border max-h-72 overflow-y-auto" align="end">
          <div className="py-1">
            {TIME_SLOTS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTime(t)}
                data-testid={`${testId}-time-${t}`}
                className={`block w-full text-left px-4 py-1.5 font-display tracking-widest text-sm transition-colors ${
                  t === time ? "bg-gold text-ink" : "text-cream hover:bg-panel-border"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
